import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Person, LocationPin, Lock, BuildRounded, GroupsRounded, CheckCircle, InfoOutlined } from "@mui/icons-material";
import { FormControl, ToggleButton, CardContent, Typography, Link, Alert } from "@mui/material";
import { Icon, IconType, InputType, MuiButton, MuiCard, MuiSelector, ACEcolors } from "@ace/ace-ui-web";
import FormField from "../../components/formfield";
import AuthHeader from '../../components/auth-header/AuthHeader';
import { useAppSelector } from '../../redux/hooks';
import { Teams } from "../../redux/TeamReducer/teamSlice";
import { Skills } from "../../redux/SkillReducer/skillSlice";
import { handlePhoneKeyDown } from '../../utils/phoneUtils';
import { useRegistrationForm } from '../../hooks/useRegistrationForm';
import { createStep1Validator, createStep2Validator } from '../../utils/registrationValidation';
import styles from "./registration.module.css";

// Import sub-components
import AdminSuccessScreen from './components/AdminSuccessScreen';
import TermsAndConditionsDialog from './components/TermsAndConditionsDialog';

const RegistrationPage: React.FC = () => {
    const { teams, loading: teamsLoading } = useAppSelector((state) => state.teams);
    const { skills, loading: skillsLoading } = useAppSelector((state) => state.skills);
    const { sites, sitesLoading, validating, checkingSuperUser } = useAppSelector((state) => state.registration);

    // Add delayed loading state
    const [showSuperUserLoading, setShowSuperUserLoading] = useState(false);

    const {
        formState,
        updateField,
        clearError,
        setErrors,
        handleContinue,
        handleSubmit,
        navigate
    } = useRegistrationForm();

    // Only show loading message if API call takes longer than 300ms
    useEffect(() => {
        if (checkingSuperUser) {
            const timer = setTimeout(() => {
                setShowSuperUserLoading(true);
            }, 300);

            return () => clearTimeout(timer);
        } else {
            setShowSuperUserLoading(false);
        }
    }, [checkingSuperUser]);

    const validateStep1 = useMemo(() => {
        const validator = createStep1Validator(
            formState.email,
            formState.workdayId,
            formState.firstName,
            formState.lastName,
            formState.site,
            formState.isSuperUser,
            formState.notificationPreferences
        );
        return () => {
            const { isValid, errors } = validator();
            setErrors(errors);
            return isValid;
        };
    }, [
        formState.email,
        formState.workdayId,
        formState.firstName,
        formState.lastName,
        formState.site,
        formState.isSuperUser,
        formState.notificationPreferences,
        setErrors
    ]);

    const validateStep2 = useMemo(() => {
        const validator = createStep2Validator(
            formState.phone,
            formState.notificationPreferences,
            formState.selectedSkills,
            formState.team,
            formState.password,
            formState.confirmPassword,
            formState.agreedToTerms,
            formState.lookedAtTerms,
            formState.firstName,
            formState.lastName,
            formState.isAdmin,
            formState.isSuperUser
        );
        return () => {
            const { isValid, errors } = validator();
            setErrors(errors);
            return isValid;
        };
    }, [
        formState.phone,
        formState.notificationPreferences,
        formState.selectedSkills,
        formState.team,
        formState.password,
        formState.confirmPassword,
        formState.agreedToTerms,
        formState.lookedAtTerms,
        formState.firstName,
        formState.lastName,
        formState.isAdmin,
        formState.isSuperUser,
        setErrors
    ]);

    const handleSkillToggle = (skillCode: string) => {
        updateField(
            'selectedSkills',
            formState.selectedSkills.includes(skillCode)
                ? formState.selectedSkills.filter(s => s !== skillCode)
                : [...formState.selectedSkills, skillCode]
        );
        clearError('skills');
    };

    const handleNotificationToggle = (method: string) => {
        updateField(
            'notificationPreferences',
            formState.notificationPreferences.includes(method)
                ? formState.notificationPreferences.filter(m => m !== method)
                : [...formState.notificationPreferences, method]
        );
        clearError('notifications');
    };

    const cardTitle = useMemo(() => {
        if (formState.step === 1) return "Personal Information";
        if (formState.step === 'admin-success') return "Check Your Email";
        if (formState.isSuperUser) return "Complete Your Super User Account";
        if (formState.isAdmin) return "Complete Your Admin Account";
        return "Additional Details";
    }, [formState.step, formState.isSuperUser, formState.isAdmin]);

    const cardSubtitle = useMemo(() => {
        if (formState.step === 1) return "Fill in your details to get started";
        if (formState.step === 'admin-success') return "Activation link sent";
        if (formState.isSuperUser || formState.isAdmin) return "Set your password to complete registration";
        return "Complete your registration";
    }, [formState.step, formState.isSuperUser, formState.isAdmin]);

    if (formState.step === 'admin-success') {
        return <AdminSuccessScreen email={formState.email} phone={formState.phone} navigate={navigate} />;
    }

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <AuthHeader title="Registration" className={styles.header} />

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <MuiCard borderRadius={2} elevation={5} padding={1} variant="elevation">
                        <div className={styles.cardTitle}>
                            <Typography variant="h6" component="h2" className={styles.cardTitleText}>
                                {cardTitle}
                            </Typography>
                            <Typography variant="body2" className={styles.cardSubtitleText}>
                                {cardSubtitle}
                            </Typography>
                        </div>
                        <CardContent>
                            <form className={styles.spaceY5}>
                                {formState.step === 1 ? (
                                    <>
                                        {/* Email Input */}
                                        <FormField
                                            label="Email"
                                            value={formState.email}
                                            onChange={(e) => {
                                                updateField('email', e.target.value);
                                                clearError('email');
                                            }}
                                            type={InputType.Email}
                                            placeholder="john.doe@example.com"
                                            icon={IconType.envelope}
                                            error={formState.errors.email}
                                        />

                                        {/* Workday ID Input */}
                                        <div className={[styles.formGroup, "form-group"].join(" ")}>
                                            <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                                <Person sx={{ height: 1, fontSize: 15, color: "gray" }} />
                                                <p className={styles.formLabel}>Workday ID</p>
                                            </div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="Enter your Workday ID"
                                                value={formState.workdayId}
                                                onChange={(e) => {
                                                    const numericValue = e.target.value.replaceAll(/\D/g, "").slice(0, 9);
                                                    updateField('workdayId', numericValue);
                                                    clearError('workdayId');
                                                }}
                                            />
                                            {formState.errors.workdayId && (
                                                <p className={styles.errorMessage}>{formState.errors.workdayId}</p>
                                            )}
                                        </div>

                                        {/* First Name & Last Name Input */}
                                        <div className={`${styles.formGrid} ${styles.formGridCols2}`}>
                                            <FormField
                                                label="First Name"
                                                value={formState.firstName}
                                                onChange={(e) => {
                                                    updateField('firstName', e.target.value);
                                                    clearError('firstName');
                                                }}
                                                type={InputType.Text}
                                                placeholder="John"
                                                muiIcon={Person}
                                                error={formState.errors.firstName}
                                            />

                                            <FormField
                                                label="Last Name"
                                                value={formState.lastName}
                                                onChange={(e) => {
                                                    updateField('lastName', e.target.value);
                                                    clearError('lastName');
                                                }}
                                                type={InputType.Text}
                                                placeholder="Doe"
                                                muiIcon={Person}
                                                error={formState.errors.lastName}
                                            />
                                        </div>

                                        {/* Sites Dropdown - hidden for super users */}
                                        {!formState.isSuperUser && (
                                            <div className={styles.formGroupSelector}>
                                                <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                                    <LocationPin sx={{ height: 1, fontSize: 15, color: "gray" }} />
                                                    <p className={styles.formLabel}>Site</p>
                                                </div>
                                                <FormControl>
                                                    <MuiSelector
                                                        placeholder="Select your site"
                                                        sx={{ height: "2.75rem", borderRadius: "calc(var(--radius) - 0.125rem)", border: " var(--color-border)" }}
                                                        onChange={(siteCd: string) => {
                                                            updateField('site', siteCd);
                                                            clearError('site');
                                                        }}
                                                        value={formState.site}
                                                        disabled={sitesLoading || checkingSuperUser}
                                                        options={sites.map((siteItem) => ({
                                                            label: `${siteItem.lookupValueCd} - ${siteItem.lookupValueDesc}`,
                                                            value: siteItem.lookupValueDesc
                                                        }))}
                                                    />
                                                </FormControl>
                                                {/* Only show loading text if it's taking a while */}
                                                {sitesLoading && (
                                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                                                        Loading sites...
                                                    </Typography>
                                                )}
                                                {showSuperUserLoading && (
                                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                                                        Checking user status...
                                                    </Typography>
                                                )}
                                                {formState.errors.site && (
                                                    <p className={styles.errorMessage}>{formState.errors.site}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Super User Indicator */}
                                        {formState.isSuperUser && (
                                            <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>Super User Access Detected</strong>
                                                    <br />
                                                    You have been identified as a super user. Site selection is not required.
                                                </Typography>
                                            </Alert>
                                        )}

                                        {/* Notification Preference */}
                                        <div className={styles.formGroup}>
                                            <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                                <Icon iconType={IconType.notification} style={{ height: 20, width: 12 }} color="gray" />
                                                <p className={styles.formLabel}>Notification Preferences</p>
                                            </div>
                                            <p className={styles.notificationHeader}>
                                                How would you like to receive <span className={styles.notificationEmphasis}>shift notifications?</span>
                                            </p>
                                            <div className={styles.toggleGroupNotifications}>
                                                {[
                                                    { value: "email", label: "Email", icon: IconType.envelope },
                                                    { value: "text", label: "Text Message", icon: IconType.phone },
                                                ].map(({ value, label, icon }) => {
                                                    const isSelected = formState.notificationPreferences.includes(value);
                                                    return (
                                                        <motion.div
                                                            whileHover={{ scale: 1.01 }}
                                                            whileTap={{ scale: 0.99 }}
                                                            key={value}
                                                        >
                                                            <ToggleButton
                                                                value={value}
                                                                selected={isSelected}
                                                                className={styles.toggleButtonNotifications}
                                                                onClick={() => handleNotificationToggle(value)}
                                                            >
                                                                <Icon iconType={icon} style={{ height: 16, width: 20 }} />
                                                                <p className="">{label}</p>
                                                            </ToggleButton>
                                                        </motion.div>
                                                    );
                                                })}
                                            </div>
                                            {formState.errors.notifications && (
                                                <p className={styles.errorMessage}>{formState.errors.notifications}</p>
                                            )}
                                        </div>

                                        <div className={styles.buttonGroup}>
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                                className={styles.buttonFull}
                                            >
                                                <MuiButton
                                                    size="large"
                                                    disabled={validating || checkingSuperUser}
                                                    onClick={() => handleContinue(validateStep1)}
                                                >
                                                    {(validating || checkingSuperUser) ? (
                                                        <motion.div
                                                            animate={{ rotate: 360 }}
                                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                            className={styles.spinner}
                                                        />
                                                    ) : (
                                                        "Continue"
                                                    )}
                                                </MuiButton>
                                            </motion.div>
                                        </div>
                                    </>
                                ) : (
                                    /* Second Page Begin */
                                    <>
                                        {/* Phone Number Input - Only show if text notifications selected */}
                                        {formState.notificationPreferences.includes("text") && (
                                            <FormField
                                                label="Phone Number"
                                                value={formState.phone}
                                                onKeyDown={handlePhoneKeyDown}
                                                onChange={(e) => {
                                                    const numericValue = e.target.value.replaceAll(/\D/g, "").slice(0, 10);
                                                    let formatted = numericValue;
                                                    if (numericValue.length > 6) {
                                                        formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
                                                    } else if (numericValue.length > 3) {
                                                        formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
                                                    }
                                                    updateField('phone', formatted);
                                                    clearError('phone');
                                                }}
                                                type={InputType.Phone}
                                                placeholder="555-123-4567"
                                                icon={IconType.phone}
                                                iconSize={{ height: 20, width: 15 }}
                                                error={formState.errors.phone}
                                            />
                                        )}

                                        {/* Skills Selection - Only show for non-admin, non-super-user users */}
                                        {!formState.isAdmin && !formState.isSuperUser && (
                                            <div className={styles.formGroup}>
                                                <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                                    <BuildRounded sx={{ height: 1, fontSize: 15, transform: 'scaleX(-1)', color: "gray" }} />
                                                    <p className={styles.formLabel}>Skills</p>
                                                </div>
                                                {(() => {
                                                    if (skillsLoading) {
                                                        return (
                                                            <div style={{ textAlign: 'center', padding: '1rem' }}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Loading skills...
                                                                </Typography>
                                                            </div>
                                                        );
                                                    }

                                                    if (Array.isArray(skills) && skills.length > 0) {
                                                        return (
                                                            <div className='toggle-group-skills'>
                                                                {skills.map((skill: Skills) => {
                                                                    const isSelected = formState.selectedSkills.includes(skill.lookupValueCd);
                                                                    return (
                                                                        <ToggleButton
                                                                            key={skill.lookupValueCd}
                                                                            value={skill.lookupValueCd}
                                                                            selected={isSelected}
                                                                            onClick={() => handleSkillToggle(skill.lookupValueCd)}
                                                                        >
                                                                            {skill.lookupValueDesc}
                                                                        </ToggleButton>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div style={{ textAlign: 'center', padding: '1rem' }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                No skills available for the selected site
                                                            </Typography>
                                                        </div>
                                                    );
                                                })()}
                                                {formState.errors.skills && (
                                                    <p className={styles.errorMessage}>{formState.errors.skills}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Team Dropdown - Only show for non-admin, non-super-user users */}
                                        {!formState.isAdmin && !formState.isSuperUser && (
                                            <div className={styles.formGroupSelector}>
                                                <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                                    <GroupsRounded sx={{ height: 1, fontSize: 15, color: "gray" }} />
                                                    <p className={styles.formLabel}>Team</p>
                                                </div>
                                                <FormControl>
                                                    <MuiSelector
                                                        placeholder="Select your team"
                                                        sx={{ height: "2.75rem", borderRadius: "calc(var(--radius) - 0.125rem)", border: " var(--color-border)" }}
                                                        onChange={(teamCd: string) => {
                                                            updateField('team', teamCd);
                                                            clearError('team');
                                                        }}
                                                        value={formState.team}
                                                        disabled={teamsLoading}
                                                        options={teams.map((teamItem: Teams) => ({
                                                            label: teamItem.lookupValueDesc,
                                                            value: teamItem.lookupValueCd
                                                        }))}
                                                    />
                                                </FormControl>
                                                {formState.errors.team && (
                                                    <p className={styles.errorMessage}>{formState.errors.team}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Password Input */}
                                        <FormField
                                            label="Password"
                                            value={formState.password}
                                            onChange={(e) => {
                                                updateField('password', e.target.value);
                                                clearError('password');
                                            }}
                                            type={InputType.Password}
                                            placeholder="Enter your password"
                                            muiIcon={Lock}
                                            helperText="Must be at least 8 characters"
                                            error={formState.errors.password}
                                        />

                                        {/* Password Confirmation Input */}
                                        <FormField
                                            label="Re-enter Password"
                                            value={formState.confirmPassword}
                                            onChange={(e) => {
                                                updateField('confirmPassword', e.target.value);
                                                clearError('confirmPassword');
                                            }}
                                            type={InputType.Password}
                                            placeholder="Re-enter your password"
                                            muiIcon={Lock}
                                            error={formState.errors.confirmPassword}
                                        />

                                        {/* Agree to Terms */}
                                        <div className={styles.termsConditions} style={{ marginBottom: '1rem' }}>
                                            <div className={styles.checkboxWrapper}>
                                                <span className={styles.termsLinkText}>
                                                    <Link
                                                        style={{
                                                            color: 'var(--color-primary-default)',
                                                            textDecorationColor: 'var(--color-primary-default)'
                                                        }}
                                                        href="#"
                                                        className={styles.termsLink}
                                                        onClick={() => updateField('showTermsDialog', true)}
                                                    >
                                                        Terms and Conditions
                                                    </Link>
                                                </span>
                                                <div className={styles.statusContainer}>
                                                    {formState.agreedToTerms ? (
                                                        <>
                                                            <span className={styles.statusText} style={{ color: 'green' }}>Reviewed and Accepted</span>
                                                            <CheckCircle className={styles.successIcon} sx={{ color: 'green', fontSize: 20 }} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className={styles.statusText} style={{ color: 'var(--color-muted-foreground)' }}>Needs Review</span>
                                                            <InfoOutlined className={styles.successIcon} sx={{ color: 'var(--color-muted-foreground)', fontSize: 20 }} />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {formState.errors.viewTerms && (
                                                <p className={styles.errorMessage}>{formState.errors.viewTerms}</p>
                                            )}
                                            {formState.errors.terms && (
                                                <p className={styles.errorMessage}>{formState.errors.terms}</p>
                                            )}
                                        </div>

                                        <div className={styles.buttonGroup}>
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                                className={styles.buttonFull}
                                            >
                                                <MuiButton
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => {
                                                        updateField('step', 1);
                                                        setErrors({});
                                                    }}
                                                    sx={{ width: '100%' }}
                                                >
                                                    Back
                                                </MuiButton>
                                            </motion.div>
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                                className={styles.buttonFull}
                                            >
                                                <MuiButton
                                                    size="large"
                                                    disabled={false}
                                                    onClick={() => handleSubmit(validateStep2)}
                                                    sx={{ width: '100%' }}
                                                >
                                                    Sign Up
                                                </MuiButton>
                                            </motion.div>
                                        </div>
                                    </>
                                )}
                            </form>
                        </CardContent>
                    </MuiCard>
                </motion.div>

                <p className={styles.footerText}>
                    By signing up, you&#39;ll receive notifications about available shifts at your selected site.
                </p>
                <p className={[styles.footerText, styles.footerLogin].join(" ")}>
                    Already have an account? <Link href="/login" underline="hover" color={ACEcolors.primary.default} sx={{ fontWeight: 'bold' }}>Sign-in here</Link>
                </p>
            </div>

            {/* Terms and Conditions Dialog */}
            <TermsAndConditionsDialog
                open={formState.showTermsDialog}
                agreedToTerms={formState.agreedToTerms}
                onClose={() => {
                    updateField('showTermsDialog', false);
                    updateField('lookedAtTerms', true);
                    clearError('viewTerms');
                }}
                onAgree={(checked: boolean) => {
                    updateField('agreedToTerms', checked);
                    clearError('terms');
                }}
            />
        </div>
    );
};

export default RegistrationPage;